if (!customElements.get('mvp-carousel')) {
  customElements.define(
    'mvp-carousel',
    class MVPCarousel extends HTMLElement {
      constructor() {
        super();

        this.track = this.querySelector('.mvp-carousel__track');
        this.originals = this.track
          ? Array.from(this.track.querySelectorAll('.mvp-carousel__item'))
          : [];
        this.section = this.closest('.mvp-carousel-section');

        if (this.originals.length === 0) {
          this.reveal();
          return;
        }

        this.stage = this.querySelector('.mvp-carousel__stage');
        this.viewport = this.querySelector('.mvp-carousel__viewport');
        this.panels = Array.from(this.querySelectorAll('.mvp-carousel__panel'));
        this.previousButton = this.querySelector('.mvp-carousel__arrow--prev');
        this.nextButton = this.querySelector('.mvp-carousel__arrow--next');
        this.variantInput = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('.mvp-carousel__submit');
        this.submitLabel = this.querySelector('.mvp-carousel__submit-label');
        this.submitPrice = this.querySelector('.mvp-carousel__submit-price');
        this.learnMore = this.querySelector('.mvp-carousel__learn-more');
        this.errorWrapper = this.querySelector(
          '.product-form__error-message-wrapper'
        );

        this.fallbackColor = this.dataset.defaultBackground || '';
        this.addToCartText = this.dataset.addToCartText || 'Add to bag';
        this.soldOutText = this.dataset.soldOutText || 'Sold out';

        this.variantCount = this.originals.length;
        this.initialIndex = parseInt(this.dataset.initialIndex, 10) || 0;

        this.measure();
        this.build();
        this.bindEvents();

        this.place(false);
        this.updateFeatured();
        this.reveal();
      }

      // Drops the loading cover once the wheel is laid out. Until this runs the
      // images are stacked in the centre of the track, so the section stays
      // hidden behind a spinner.
      reveal() {
        if (this.section) {
          this.section.classList.add('mvp-carousel-section--ready');
        }
      }

      // How many images fit each side of the featured one. Depends on the
      // viewport, so it is re-derived whenever that changes.
      measure() {
        const styles = getComputedStyle(this);
        const width =
          parseFloat(styles.getPropertyValue('--mvp-carousel-width')) || 360;
        const step =
          parseFloat(styles.getPropertyValue('--mvp-carousel-step')) || 400;
        const available = this.viewport ? this.viewport.clientWidth : 0;

        // An image counts as showing as soon as any sliver of it is on screen.
        this.visibleRadius = Math.max(
          1,
          Math.ceil((available / 2 + width / 2) / step) - 1
        );
      }

      /*
        The loop is real: the variant list is repeated into a longer strip, and
        the wheel is one transform on the track rather than a transform per
        image. Because the strip repeats every `variantCount` slots, shifting
        the position by a whole number of periods is pixel-identical on screen,
        which is what lets it spin forever without anything visibly jumping.
      */
      build() {
        // Blocks of padding each side, enough that the visible window never
        // runs off either end of the strip before it is re-based.
        const blocks = Math.max(
          1,
          Math.ceil((this.visibleRadius * 2 + 1) / this.variantCount)
        );

        if (this.blocks === blocks) return;
        this.blocks = blocks;

        this.track
          .querySelectorAll('.mvp-carousel__item[data-clone]')
          .forEach((clone) => clone.remove());

        if (this.variantCount > 1) {
          const before = document.createDocumentFragment();
          const after = document.createDocumentFragment();

          for (let block = 0; block < blocks; block += 1) {
            this.originals.forEach((item) => {
              before.appendChild(MVPCarousel.copy(item));
              after.appendChild(MVPCarousel.copy(item));
            });
          }

          this.track.insertBefore(before, this.originals[0]);
          this.track.appendChild(after);
        }

        this.items = Array.from(
          this.track.querySelectorAll('.mvp-carousel__item')
        );
        this.base = this.variantCount > 1 ? blocks * this.variantCount : 0;
        this.position = this.base + this.initialIndex;

        // Each image keeps a fixed slot on the strip for its whole life; only
        // the track moves.
        this.items.forEach((item, slot) =>
          item.style.setProperty('--mvp-carousel-slot', slot)
        );
      }

      static copy(item) {
        const clone = item.cloneNode(true);
        clone.dataset.clone = 'true';
        clone.removeAttribute('inert');

        const button = clone.querySelector('.mvp-carousel__item-button');
        if (button) button.removeAttribute('aria-current');

        return clone;
      }

      bindEvents() {
        this.track.addEventListener('click', (event) => {
          const button = event.target.closest('.mvp-carousel__item-button');
          if (!button || this.suppressClick) return;

          const item = button.closest('.mvp-carousel__item');
          this.spin(this.items.indexOf(item) - this.position);
        });

        if (this.previousButton) {
          this.previousButton.addEventListener('click', () => this.spin(-1));
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => this.spin(1));
        }

        if (this.stage) {
          this.stage.addEventListener('keydown', this.onKeydown.bind(this));
        }

        if (this.viewport && this.variantCount > 1) {
          this.viewport.addEventListener(
            'pointerdown',
            this.onPointerDown.bind(this)
          );
          this.viewport.addEventListener(
            'pointerup',
            this.onPointerUp.bind(this)
          );
          this.viewport.addEventListener('pointercancel', () => {
            this.pointerStart = null;
          });

          if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.onResize.bind(this));
            this.resizeObserver.observe(this.viewport);
          }
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      onResize() {
        if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);

        this.resizeFrame = requestAnimationFrame(() => {
          const radius = this.visibleRadius;
          this.measure();
          if (radius === this.visibleRadius) return;

          // A wider viewport can need a longer strip to pad the visible window.
          this.initialIndex = this.variantIndex;
          this.build();
          this.place(false);
          this.updateFeatured();
        });
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.spin(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.spin(1);
        }
      }

      onPointerDown(event) {
        this.suppressClick = false;
        this.pointerStart = { x: event.clientX, y: event.clientY };
      }

      onPointerUp(event) {
        if (!this.pointerStart) return;

        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;

        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY))
          return;

        // A swipe ends in a click on whichever image was under the finger, so
        // swallow that click rather than letting it fight the swipe.
        this.suppressClick = true;
        this.spin(deltaX < 0 ? 1 : -1);
      }

      spin(delta) {
        if (delta === 0 || this.variantCount < 2) return;

        // Slide the strip back under the featured image first. This is
        // invisible (a whole number of periods) and keeps the position from
        // ever drifting off the end, so the wheel turns the same way forever.
        this.rebase();

        this.position += delta;
        this.place(true);
        this.updateFeatured();
      }

      rebase() {
        const period = this.variantCount;
        const rebased =
          this.base + MVPCarousel.mod(this.position - this.base, period);
        if (rebased === this.position) return;

        this.position = rebased;
        this.place(false);
      }

      place(animate = true) {
        // A re-base swaps which copies of the strip are on screen, so the
        // images' fade has to be frozen alongside the track's slide.
        if (!animate) this.track.classList.add('mvp-carousel__track--instant');

        this.track.style.setProperty('--mvp-carousel-position', this.position);

        this.items.forEach((item, slot) => {
          const distance = Math.abs(slot - this.position);
          const hidden = distance > this.visibleRadius;

          // Fetch one slot beyond the edge so the next image is already there
          // when it slides in.
          if (distance <= this.visibleRadius + 1) {
            const image = item.querySelector('.mvp-carousel__image');
            if (image) image.loading = 'eager';
          }

          item.classList.toggle(
            'mvp-carousel__item--featured',
            slot === this.position
          );
          item.classList.toggle('mvp-carousel__item--hidden', hidden);
          item.inert = hidden;

          const button = item.querySelector('.mvp-carousel__item-button');
          if (!button) return;

          button.tabIndex = hidden ? -1 : 0;
          if (slot === this.position) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });

        if (!animate) {
          this.forceReflow();
          this.track.classList.remove('mvp-carousel__track--instant');
        }
      }

      get variantIndex() {
        return MVPCarousel.mod(this.position, this.variantCount);
      }

      updateFeatured() {
        const item = this.items[this.position];
        const index = this.variantIndex;
        const available = item.dataset.available === 'true';
        const color = item.dataset.shadeColor || this.fallbackColor;

        this.panels.forEach((panel, panelIndex) => {
          panel.hidden = panelIndex !== index;
        });

        if (this.section && color) {
          this.section.style.setProperty('--mvp-carousel-bg', color);
        }

        if (this.variantInput) this.variantInput.value = item.dataset.variantId;

        if (this.submitButton) {
          this.submitButton.disabled = !available;
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
        }

        if (this.submitLabel) {
          this.submitLabel.textContent = available
            ? this.addToCartText
            : this.soldOutText;
        }

        if (this.submitPrice) {
          this.submitPrice.textContent = item.dataset.price || '';
        }

        if (this.learnMore && item.dataset.url) {
          this.learnMore.href = item.dataset.url;
        }

        if (this.errorWrapper) this.errorWrapper.hidden = true;
      }

      forceReflow() {
        // eslint-disable-next-line no-unused-expressions
        this.track.offsetWidth;
      }

      static mod(value, size) {
        return ((value % size) + size) % size;
      }
    }
  );
}
